import type { ComponentPropsWithRef, ReactNode } from 'react';
import { Spinner } from '@src/components/Spinner/Spinner';
import { testIdAttr } from '@src/components/testId';
import { Typography, type TypeRole, type TypeSize } from '@src/components/Typography/Typography';
import './Button.css';

/**
 * Primary is the coral accent action; blue is a filled supporting action.
 * Both share the same sizing, typography, and interaction behavior.
 */
export type ButtonVariant = 'primary' | 'blue' | 'secondary' | 'soft' | 'ghost' | 'danger' | 'danger-outline' | 'premium-dark' | 'premium-light';

export type ButtonSize = 'small' | 'medium' | 'large';

export type ButtonIconPosition = 'start' | 'end';

/** `style` is omitted on purpose: appearance comes from Button.css, never inline. */
export interface ButtonProps extends Omit<ComponentPropsWithRef<'button'>, 'style'> {
  /**
   * The label. Omit it (and pass `icon` plus an `aria-label`) for a square
   * icon-only button — close, more, back.
   */
  children?: ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Decorative glyph or SVG. Sized by the button, and hidden from screen readers. */
  icon?: ReactNode;
  iconPosition?: ButtonIconPosition;
  /**
   * Swaps the icon for a spinner and stops taking clicks, while the label stays
   * put so the button keeps its width — a request in flight must not reflow the
   * row it sits in.
   */
  loading?: boolean;
  fullWidth?: boolean;
  testId?: string;
}

function buttonTypeSize(size: ButtonSize): TypeSize {
  if (size === 'small') {
    return 'sm';
  }
  if (size === 'large') {
    return 'xl';
  }
  return 'lg';
}

/** Role (and size, when the role is a range) for this variant and size. */
function buttonLabelType(
  variant: ButtonVariant,
  size: ButtonSize,
): { role: TypeRole; size?: TypeSize } {
  const typeSize = buttonTypeSize(size);
  if (variant === 'ghost') {
    return { role: 'label/ghost', size: typeSize };
  }
  if (variant === 'secondary' || variant === 'soft' || variant === 'danger-outline') {
    return { role: 'label/button-secondary', size: typeSize };
  }
  if (size === 'small') {
    return { role: 'label/button-sm' };
  }
  if (size === 'large') {
    return { role: 'label/button-lg' };
  }
  return { role: 'label/button-md' };
}

export function Button({
  children,
  variant = 'primary',
  size = 'medium',
  icon,
  iconPosition = 'start',
  loading = false,
  fullWidth = false,
  className,
  type = 'button',
  disabled,
  testId,
  ...rest
}: ButtonProps) {
  const hasLabel = children != null && children !== '';
  const glyph = loading ? <Spinner /> : icon;
  const iconOnly = Boolean(glyph) && !hasLabel;

  const classes = ['button', `button--${variant}`, `button--${size}`];
  if (fullWidth) {
    classes.push('button--full-width');
  }
  if (iconOnly) {
    classes.push('button--icon-only');
  }
  if (className) {
    classes.push(className);
  }

  // The icon is placed in the DOM on the side it renders on, rather than being
  // moved with CSS, so the reading order matches the visual order.
  const iconNode = glyph ? (
    <span className="button__icon" aria-hidden="true">
      {glyph}
    </span>
  ) : null;

  return (
    <button
      className={classes.join(' ')}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...testIdAttr(testId)}
      {...rest}
    >
      {iconPosition === 'start' && iconNode}
      {hasLabel && (
        <Typography
          as="span"
          className="button__label"
          color="inherit"
          {...buttonLabelType(variant, size)}
        >
          {children}
        </Typography>
      )}
      {iconPosition === 'end' && iconNode}
    </button>
  );
}
